import {IcnTriangleWarning} from "../../assets/icons";

import React, {useState, useEffect} from 'react';
import {useLocation} from "react-router-dom";
import * as Yup from "yup";
import {Formik, Form, FormikHelpers} from "formik";

import {useAuth} from "../../providers/AuthProvider";

import FormFieldset from "../form-elements/FormFieldset";
import FormGroupErrorText from "../form-elements/FormGroupErrorText";
import FormSubmitValidation from "../form-elements/FormSubmitValidation";
import Button from "../common/Button";
import ButtonLink from "../common/ButtonLink";
import CustomLink from "../common/CustomLink";
import Alert from "../common/Alert";
import FormInput from "../form-elements/FormInput";

interface FormValues {
  email: string;
}

const ResetPasswordFormSchema = Yup.object().shape({
  email: Yup.string().email('Please enter valid email').required('Please enter email'),
});

function ResetPasswordForm() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const { requestPasswordReset } = useAuth();
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location.state]);
  const handleSubmit = async (
    values: FormValues,
    { setSubmitting, setStatus }: FormikHelpers<FormValues>
  ) => {
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      await requestPasswordReset(values.email);
      setSuccess(true);
      setStatus('submitted');
    } catch (err: any) {
      if (err.response) {
        setError(err.response.data.message || "An error occurred while resetting your password. Please try again later.");
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("An error occurred during password reset.");
      }
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        email: "",
      }}
      validateOnMount={true}
      validateOnChange={true}
      validationSchema={ResetPasswordFormSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, status, errors, touched, dirty, isValid }) => {

        return (
          <>
            {status === "submitted" && success ? (
              <FormSubmitValidation type="success">
                <h1 className="h5">
                  An email was sent!
                </h1>

                <p>
                  We've emailed you instructions for setting your new password, if an account exists with the email you entered. You should receive them shortly. Please make sure to check your spam folder.
                </p>

                <ButtonLink url="/login" btnSize='lg' text="Continue"/>
              </FormSubmitValidation>
            ):(

              <Form
                className="c-form"
                noValidate
              >
                <FormFieldset
                  extraClassName="authentication"
                  legendText='Enter your email'
                >

                  <FormInput
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    labelText="Email"
                    type="email"
                    name="email"
                    placeholder="Enter email"
                    autoComplete="email"
                    hideError
                  />

                  <CustomLink
                    extraClassName="c-authenticate-form-link"
                    url="/login"
                    text="Go back to Login"
                    linkStyle="reverse"
                  />

                  {errors.email && touched.email && (
                    <FormGroupErrorText text={errors.email}/>
                  )}

                </FormFieldset>

                <Button
                  type="submit"
                  btnSize="lg"
                  disabled={!isValid || isSubmitting || !dirty}
                  isLoading={isLoading}
                  isLoadingText="Resetting password"
                  text="Reset my password"
                />

                {error && (
                  <Alert
                    type="danger"
                    alertRadius="rounded12px"
                    alertStyle="bordered"
                    icon={<IcnTriangleWarning/>}
                    text={error}
                  />
                )}

              </Form>
            )}
          </>
        )
      }}
    </Formik>
  );
}

export default ResetPasswordForm;
